import { useTranslation } from 'react-i18next'
import { LiveWaveform } from '@/components/ui/LiveWaveform'

type VoiceRecorderBarProps = {
  elapsed: number
  stream: MediaStream | null
  /** Mic not live yet: gray affordance, no timer — tells the user to wait before speaking. */
  starting?: boolean
}

const formatElapsed = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`

/** Middle slot of the composer while recording: red dot + timer + live waveform.
 *  Buttons live in the composer addons so the row keeps the exact same anatomy
 *  (and height) as the normal input. */
export const VoiceRecorderBar = ({ elapsed, stream, starting }: VoiceRecorderBarProps) => {
  const { t } = useTranslation()

  if (starting) {
    return (
      <div className="flex min-h-9 min-w-0 flex-1 items-center gap-1.5 self-stretch px-3">
        <span aria-hidden className="size-2 animate-pulse rounded-full bg-muted-foreground/50" />
        <span role="status" className="truncate text-sm text-muted-foreground">
          {t('voiceStarting')}
        </span>
      </div>
    )
  }

  return (
    <div className="flex min-h-9 min-w-0 flex-1 items-center gap-2.5 self-stretch px-3">
      <span role="status" className="flex shrink-0 items-center gap-1.5 text-sm text-muted-foreground">
        <span aria-hidden className="size-2 animate-pulse rounded-full bg-destructive" />
        <span className="tabular-nums">{formatElapsed(elapsed)}</span>
        <span className="sr-only">{t('voiceRecording')}</span>
      </span>
      {stream && <LiveWaveform stream={stream} />}
    </div>
  )
}
